import { useStaffBusinessUnits } from "@/hooks/use-staff-business-units";
import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { format, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, addHours } from "date-fns";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Card } from "@/components/ui/card";
import {
  Calendar as FullCalendar,
  type CalendarEvent,
  type CalendarViewType,
} from "@/components/ui/full-calendar/calendar";
import FacilityScheduleForm from "../modal/form";
import { FacilitySchedule } from "@/types/facility/schedule";
import { FacilityBooking } from "@/types/facility/booking";
import {
  useGetAllFacilityScheduleSlotQuery,
  useGetAllFacilityBookingsQuery,
  useDeleteFacilityScheduleMutation,
  useDeleteFacilityBookingMutation,
  useLazyGetFacilityScheduleSlotDetailsQuery,
  useGetFacilitiesQuery,
} from "@/services/facilityApis";
import { useCancelSessionSlotMutation } from "@/services/trainingApi";
import BookingRequestDetails from "@/components/admin/facility_bookings/modal/booking-details";
import { TrainingSessionSheet } from "./training-session-sheet";
import { parseSessionDateTime, isTrainingBooking } from "@/lib/helper";
import { toast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import type { DeleteScope } from "@/components/ui/full-calendar/calendar-event-actions";
import { deriveEndDateFromCount } from "@/utils/scheduleUtils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { MultiSelect } from "@/components/ui/multiselect/multiselectCheckbox";
import { Button } from "@/components/ui/button";
import { useSearchParams } from "react-router-dom";
import { useGetCoachesQuery } from "@/services/coachApi";
import { useGetBusinessUnitsQuery } from "@/services/businessUnitApi";

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────
const colorFromString = (colorClass?: string): string => {
  if (!colorClass) return "blue";
  return colorClass.split("-")[0];
};

/** "2026-04-20T14:00:00" → "14:00" */
const extractTime = (iso: string): string => {
  if (!iso) return "";
  if (iso.includes("T")) return iso.split("T")[1].substring(0, 5);
  return iso.substring(0, 5);
};

/** "2026-04-20T14:00:00" → "2026-04-20" */
const extractDate = (iso: string): string => {
  if (!iso) return "";
  if (iso.includes("T")) return iso.split("T")[0];
  return iso;
};

/**
 * Parse rrule_str into RecurrenceConfig object that RecurrenceSelector expects.
 *
 * Example input:
 *   "RRULE:FREQ=WEEKLY;BYDAY=MO,WE;UNTIL=20260507T000000Z"
 *
 * Output:
 *   {
 *     frequency: "weekly",
 *     interval: 1,
 *     weekDays: ["MO", "WE"],
 *     endCondition: "on_date",
 *     endDate: "2026-05-07",
 *     occurrences: undefined,
 *   }
 */
const parseRRuleStr = (rruleStr: string): Record<string, any> | undefined => {
  if (!rruleStr) return undefined;

  // Strip "RRULE:" prefix if present
  const raw = rruleStr.replace(/^RRULE:/i, "");

  // Parse key=value pairs
  const parts: Record<string, string> = {};
  raw.split(";").forEach((part) => {
    const [key, val] = part.split("=");
    if (key && val !== undefined) parts[key.toUpperCase()] = val;
  });

  const frequency = (parts["FREQ"] ?? "").toLowerCase(); // "weekly", "daily", etc.
  const interval = parts["INTERVAL"] ? parseInt(parts["INTERVAL"], 10) : 1;

  // BYDAY → array of day codes e.g. ["MO", "WE"]
  const mapRecurrenceDay = (dayCode: string, full: boolean = false) => {
    const normalized = dayCode.trim().toUpperCase();
    const code = normalized.replace(/^[-+]?\d+/, "");
    const map: Record<string, string> = {
      MO: full ? "Monday" : "Mon",
      TU: full ? "Tuesday" : "Tue",
      WE: full ? "Wednesday" : "Wed",
      TH: full ? "Thursday" : "Thu",
      FR: full ? "Friday" : "Fri",
      SA: full ? "Saturday" : "Sat",
      SU: full ? "Sunday" : "Sun",
    };
    return map[code] ?? dayCode.trim();
  };

  const weekDays = parts["BYDAY"] && frequency !== "monthly"
    ? parts["BYDAY"].split(",").map((d) => mapRecurrenceDay(d))
    : [];

  // Monthly specific parsing
  let monthlyType: "day" | "pattern" = "day";
  let monthlyDay: number = 1;
  let monthlyPattern: any = { week: "first", day: "Monday" };

  if (frequency === "monthly") {
    if (parts["BYMONTHDAY"]) {
      monthlyType = "day";
      monthlyDay = parseInt(parts["BYMONTHDAY"], 10);
    } else if (parts["BYDAY"]) {
      monthlyType = "pattern";
      const rawByDay = parts["BYDAY"];
      const match = rawByDay.match(/^([-+]?\d+)([A-Z]{2})$/);
      if (match) {
        const pos = match[1];
        const dayCode = match[2];
        const posMap: Record<string, string> = {
          "1": "first",
          "2": "second",
          "3": "third",
          "4": "fourth",
          "-1": "last",
        };
        monthlyPattern = {
          week: posMap[pos] || "first",
          day: mapRecurrenceDay(dayCode, true),
        };
      }
    }
  }

  let endCondition: string = "after";
  let endDate: string | undefined;
  let occurrences: number | undefined;

  if (parts["UNTIL"]) {
    endCondition = "on_date";
    const u = parts["UNTIL"].replace(/T.*$/, ""); // "20260507"
    endDate = `${u.substring(0, 4)}-${u.substring(4, 6)}-${u.substring(6, 8)}`;
  } else if (parts["COUNT"]) {
    endCondition = "after";
    occurrences = parseInt(parts["COUNT"], 10);
  }

  if (!frequency) return undefined;

  return {
    enabled: true,
    frequency,
    interval,
    weekDays,
    monthlyType,
    monthlyDay,
    monthlyPattern,
    endCondition,
    endDate,
    occurrences,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────────────────────
const FacilityScheduleCalendarView: React.FC = () => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const facilityScheduleAccess = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .facility_schedule ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

   // ── URL search parameters ──────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const urlBusinessUnitId = searchParams.get("business_unit_id");
  const urlFacilityId = searchParams.get("facility_id");
  const urlCoachId = searchParams.get("coach_id");
  const urlDate = searchParams.get("date");

  // ── Modal / form state ──────────────────────────────────────────────
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit" | "view">("add");
  const [formData, setFormData] = useState<FacilitySchedule | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // ── Training Sheet & Booking Details modal states ───────────────────
  const [isTrainingSheetOpen, setIsTrainingSheetOpen] = useState(false);
  const [selectedTrainingBooking, setSelectedTrainingBooking] = useState<FacilityBooking | Record<string, any> | null>(null);
  const [isBookingDetailsOpen, setIsBookingDetailsOpen] = useState(false);
  const [selectedFacilityBooking, setSelectedFacilityBooking] = useState<FacilityBooking | null>(null);

  const [cancelSessionSlot] = useCancelSessionSlotMutation();
  const [deleteFacilityBooking] = useDeleteFacilityBookingMutation();

// ── Calendar navigation & View ──────────────────────────────────────
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    if (urlDate) {
      const parsed = new Date(urlDate);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  const [calendarView, setCalendarView] = useState<CalendarViewType>(
    urlDate ? "week" : "month"
  );

  // ── Filter sheet state ──────────────────────────────────────────────
  const { isSuperAdmin, assignedBusinessUnitIds } = useStaffBusinessUnits();
  const userAssignedBuIds = useMemo(
    () => assignedBusinessUnitIds.map(String),
    [assignedBusinessUnitIds]
  );

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [pendingBusinessUnitIds, setPendingBusinessUnitIds] = useState<string[]>(
    urlBusinessUnitId ? [urlBusinessUnitId] : []
  );
  const [appliedBusinessUnitIds, setAppliedBusinessUnitIds] = useState<string[]>(
    urlBusinessUnitId ? [urlBusinessUnitId] : []
  );
  const [pendingFacilityIds, setPendingFacilityIds] = useState<string[]>(
    urlFacilityId ? [urlFacilityId] : []
  );
  const [appliedFacilityIds, setAppliedFacilityIds] = useState<string[]>(
    urlFacilityId ? [urlFacilityId] : []
  );
  const [pendingCoachIds, setPendingCoachIds] = useState<string[]>(
    urlCoachId ? [urlCoachId] : []
  );
  const [appliedCoachIds, setAppliedCoachIds] = useState<string[]>(
    urlCoachId ? [urlCoachId] : []
  );

  // Sync state if URL search params change
  useEffect(() => {
    if (urlDate) {
      const parsed = new Date(urlDate);
      if (!isNaN(parsed.getTime())) {
        setCurrentDate(parsed);
        setCalendarView("week");
      }
    }
  }, [urlDate]);

  useEffect(() => {
    if (urlBusinessUnitId) {
      if (
        isSuperAdmin ||
        userAssignedBuIds.length === 0 ||
        userAssignedBuIds.includes(urlBusinessUnitId)
      ) {
        setPendingBusinessUnitIds([urlBusinessUnitId]);
        setAppliedBusinessUnitIds([urlBusinessUnitId]);
      }
    }
    if (urlFacilityId) {
      setPendingFacilityIds([urlFacilityId]);
      setAppliedFacilityIds([urlFacilityId]);
    }
    if (urlCoachId) {
      setPendingCoachIds([urlCoachId]);
      setAppliedCoachIds([urlCoachId]);
    }
  }, [urlBusinessUnitId, urlFacilityId, urlCoachId, isSuperAdmin, userAssignedBuIds]);

  // ── Fetch business units for filter dropdown ─────────────────────────
  const { data: businessUnitsData } = useGetBusinessUnitsQuery(
    { org_id: Number(orgId), limit: 100 },
    { skip: !orgId }
  );

  const businessUnitOptions = useMemo(() => {
    if (!Array.isArray(businessUnitsData?.data)) return [];
    let list = businessUnitsData.data;
    if (!isSuperAdmin && userAssignedBuIds.length > 0) {
      list = list.filter((bu: any) => userAssignedBuIds.includes(String(bu.id)));
    }
    return list.map((bu: any) => ({
      value: String(bu.id),
      label: bu.name,
    }));
  }, [businessUnitsData, isSuperAdmin, userAssignedBuIds]);

  // Default to the first business unit on initial load
  const hasInitializedDefaultBURef = useRef(false);
  useEffect(() => {
    if (businessUnitOptions.length > 0) {
      if (!hasInitializedDefaultBURef.current) {
        hasInitializedDefaultBURef.current = true;
        const validUrlBu =
          urlBusinessUnitId &&
          (isSuperAdmin ||
            userAssignedBuIds.length === 0 ||
            userAssignedBuIds.includes(urlBusinessUnitId));
        if (!validUrlBu && appliedBusinessUnitIds.length === 0) {
          const firstBuId = businessUnitOptions[0].value;
          setAppliedBusinessUnitIds([firstBuId]);
          setPendingBusinessUnitIds([firstBuId]);
        }
      }
      // If currently selected BU is not in allowed list for scoped user, enforce first valid option
      if (
        !isSuperAdmin &&
        userAssignedBuIds.length > 0 &&
        appliedBusinessUnitIds.length > 0
      ) {
        const invalidSelected = appliedBusinessUnitIds.some(
          (id) => !userAssignedBuIds.includes(id)
        );
        if (invalidSelected) {
          const firstValid = businessUnitOptions[0]?.value
            ? [businessUnitOptions[0].value]
            : [];
          setAppliedBusinessUnitIds(firstValid);
          setPendingBusinessUnitIds(firstValid);
        }
      }
    }
  }, [
    businessUnitOptions,
    urlBusinessUnitId,
    appliedBusinessUnitIds,
    isSuperAdmin,
    userAssignedBuIds,
  ]);

  // ── Fetch facilities for filter dropdown (filtered by selected business units) ────
  const facilitiesQueryStr = useMemo(() => {
    const params = new URLSearchParams({ sort_key: "id", sort_order: "desc" });
    const buIds = isFilterOpen ? pendingBusinessUnitIds : appliedBusinessUnitIds;
    if (buIds.length > 0) {
      params.set("business_unit_id", buIds.join(","));
    }
    return params.toString();
  }, [isFilterOpen, pendingBusinessUnitIds, appliedBusinessUnitIds]);

  const { data: facilitiesData } = useGetFacilitiesQuery(
    { orgId, query: facilitiesQueryStr },
    { skip: !orgId }
  );

  const facilityOptions = useMemo(() => {
    return Array.isArray(facilitiesData?.data)
      ? facilitiesData.data.map((f: any) => ({
          value: String(f.id),
          label: f.parent_name ? `${f.parent_name} › ${f.name}` : f.name,
        }))
      : [];
  }, [facilitiesData]);

   // ── Fetch coaches for filter dropdown ───────────────────────────────
  const { data: coachesData } = useGetCoachesQuery(
    { org_id: Number(orgId), query: "status=active&limit=100" },
    { skip: !orgId }
  );

  const coachOptions = useMemo(() => {
    return Array.isArray(coachesData?.data)
      ? coachesData.data.map((c: any) => {
          const coachName =
            c.full_name ||
            [c.first_name, c.last_name].filter(Boolean).join(" ") ||
            c.name ||
            c.coach_name ||
            `Coach #${c.id}`;
          return {
            value: String(c.id),
            label: coachName,
          };
        })
      : [];
  }, [coachesData]);

  const coachMap = useMemo(() => {
    const map: Record<string, string> = {};
    coachOptions.forEach((c) => {
      map[c.value] = c.label;
    });
    return map;
  }, [coachOptions]);


  const handlePrevious = useCallback(() => {
    setCurrentDate((prev) => {
      if (calendarView === "month") return subMonths(prev, 1);
      if (calendarView === "week") return subWeeks(prev, 1);
      return subDays(prev, 1);
    });
  }, [calendarView]);

  const handleNext = useCallback(() => {
    setCurrentDate((prev) => {
      if (calendarView === "month") return addMonths(prev, 1);
      if (calendarView === "week") return addWeeks(prev, 1);
      return addDays(prev, 1);
    });
  }, [calendarView]);

  // ── API: fetch slots for visible month ──────────────────────────────
  const queryString = useMemo(() => {
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const params = new URLSearchParams({
      start_date: format(monthStart, "yyyy-MM-dd"),
      end_date: format(monthEnd, "yyyy-MM-dd"),
      load_consumed_slots: "true",
    });
    if (appliedBusinessUnitIds.length > 0) {
      params.set("business_unit_id", appliedBusinessUnitIds.join(","));
    }
    if (appliedFacilityIds.length > 0) {
      params.set("facility_id", appliedFacilityIds.join(","));
    }
    if (appliedCoachIds.length > 0) {
      params.set("coach_id", appliedCoachIds.join(","));
    }
    return params.toString();
  }, [currentDate, appliedBusinessUnitIds, appliedFacilityIds, appliedCoachIds]);

  const { data: slotsData, isLoading, refetch } = useGetAllFacilityScheduleSlotQuery(
    { query: queryString, orgId },
    { skip: !orgId }
  );

  // ── API: fetch actual bookings for visible month (includes training sessions & facility bookings) ──
  const bookingsQueryString = useMemo(() => {
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const params = new URLSearchParams({
      start_date: format(monthStart, "yyyy-MM-dd"),
      end_date: format(monthEnd, "yyyy-MM-dd"),
      limit: "500",
      view_type: "admin",
    });
    if (appliedBusinessUnitIds.length > 0) {
      params.set("business_unit_ids", appliedBusinessUnitIds.join(","));
    }
    if (appliedFacilityIds.length > 0) {
      params.set("facility_ids", appliedFacilityIds.join(","));
    }
    if (appliedCoachIds.length > 0) {
      params.set("coach_id", appliedCoachIds[0]);
    }
    return params.toString();
  }, [currentDate, appliedBusinessUnitIds, appliedFacilityIds, appliedCoachIds]);

  const { data: bookingsData, refetch: bookingsRefetch } = useGetAllFacilityBookingsQuery(
    { query: bookingsQueryString, orgId: Number(orgId) },
    { skip: !orgId }
  );

  const [deleteFacilitySchedule] = useDeleteFacilityScheduleMutation();

  // ── Lazy query: fetch full schedule details when edit is clicked ─────
  const [getSlotDetails] = useLazyGetFacilityScheduleSlotDetailsQuery();

  // ── Transform API slots + bookings → CalendarEvents ───────────────────────────
  const events: CalendarEvent[] = useMemo(() => {
    const combinedEvents: CalendarEvent[] = [];
    const existingSlotKeys = new Set<string>();
    const matchedBookingIds = new Set<number>();

    const bookingList = Array.isArray(bookingsData?.data) ? bookingsData.data : [];

    // Pre-process and index bookings for fast slot matching
    const bookingsBySlotId = new Map<number, any>();
    const bookingsByFacilityDateTime = new Map<string, any>();
    const bookingsByScheduleDateTime = new Map<string, any>();
    const parsedBookingDates = new Map<any, { start: Date; end: Date }>();

    bookingList.forEach((booking: any) => {
      try {
        if (booking.is_deleted) return;
        const statusLower = String(booking.status || "").toLowerCase();
        if (statusLower === "cancelled" || statusLower === "rejected") return;

        let bStart: Date | null = null;
        let bEnd: Date | null = null;
        if (booking.slot_start_time) {
          bStart = parseSessionDateTime(booking.slot_start_time) || new Date(booking.slot_start_time);
        } else if (booking.booking_date && booking.start_time) {
          bStart = parseSessionDateTime(`${booking.booking_date}T${booking.start_time}`) || new Date(`${booking.booking_date}T${booking.start_time}`);
        }
        if (!bStart || isNaN(bStart.getTime())) return;

        if (booking.slot_end_time) {
          bEnd = parseSessionDateTime(booking.slot_end_time) || new Date(booking.slot_end_time);
        } else if (booking.booking_date && booking.end_time) {
          bEnd = parseSessionDateTime(`${booking.booking_date}T${booking.end_time}`) || new Date(`${booking.booking_date}T${booking.end_time}`);
        } else {
          bEnd = addHours(bStart, 1);
        }

        const bDateStr = format(bStart, "yyyy-MM-dd");
        const bTimeHHmm = format(bStart, "HH:mm");

        const enrichedBooking = {
          ...booking,
          _parsedStart: bStart,
          _parsedEnd: bEnd,
        };

        if (booking.id != null) {
          parsedBookingDates.set(booking.id, { start: bStart, end: bEnd });
        }

        if (booking.schedule_slot_id) {
          bookingsBySlotId.set(Number(booking.schedule_slot_id), enrichedBooking);
        }
        if (booking.facility_id) {
          bookingsByFacilityDateTime.set(`${booking.facility_id}_${bDateStr}_${bTimeHHmm}`, enrichedBooking);
        }
        if (booking.facility_schedule_id) {
          bookingsByScheduleDateTime.set(`${booking.facility_schedule_id}_${bDateStr}_${bTimeHHmm}`, enrichedBooking);
        }
      } catch (err) {
        console.error("Failed to parse calendar booking:", booking, err);
      }
    });

    if (Array.isArray(slotsData?.data)) {
      slotsData.data.forEach((slot: any) => {
        try {
          const start = new Date(`${slot.slot_date}T${slot.start_time}`);
          const end = new Date(`${slot.slot_date}T${slot.end_time}`);
          if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

          const slotDateStr = slot.slot_date || format(start, "yyyy-MM-dd");
          const slotTimeHHmm = (slot.start_time || "").slice(0, 5) || format(start, "HH:mm");
          const endTimeHHmm = (slot.end_time || "").slice(0, 5) || format(end, "HH:mm");
          const slotTimeKey = `${slot.facility_id}_${slotDateStr}_${slotTimeHHmm}`;
          const schedTimeKey = `${slot.facility_schedule_id}_${slotDateStr}_${slotTimeHHmm}`;

          // Match booking by slot_id, facility+datetime, schedule+datetime, or slot.consumed_slots
          const matchedBooking =
            (slot.id && bookingsBySlotId.get(Number(slot.id))) ||
            bookingsByFacilityDateTime.get(slotTimeKey) ||
            bookingsByScheduleDateTime.get(schedTimeKey) ||
            (slot.consumed_slots && slot.consumed_slots.length > 0 ? slot.consumed_slots[0] : null);

          const isConsumed = Boolean(matchedBooking);
          if (matchedBooking?.id) {
            matchedBookingIds.add(Number(matchedBooking.id));
          }

          const isConsumedTraining = isTrainingBooking(matchedBooking) || isTrainingBooking(slot);

          const color = isConsumed ? "red" : colorFromString(slot.color);
          const eventId = matchedBooking?.id
            ? `booking_${matchedBooking.id}`
            : (slot.id ? String(slot.id) : `${slot.facility_schedule_id}_${slot.slot_date}_${slot.start_time}`);

          // Track slot keys to prevent duplicate rendering from bookingList
          existingSlotKeys.add(slotTimeKey);
          existingSlotKeys.add(`${slot.facility_id}_${slot.slot_date}_${slot.start_time}`);

          const tagLabel = isConsumed
            ? (isConsumedTraining ? "Reserved" : (matchedBooking?.status ? matchedBooking.status.toUpperCase() : "Booked"))
            : "Available";

          const title = isConsumedTraining && (matchedBooking?.training_package_name || matchedBooking?.facility_name)
            ? `${matchedBooking.training_package_name || matchedBooking.facility_name} – ${matchedBooking.member_name || "Reserved"}`
            : isConsumed && matchedBooking?.member_name
            ? `${slot.facility_name || slot.name || "Facility"} – ${matchedBooking.member_name}`
            : `${slot.facility_name || slot.name || "Facility"}  ${slotTimeHHmm} – ${endTimeHHmm}`;

          const user = isConsumed
            ? {
                name: matchedBooking?.member_name || (isConsumedTraining ? "Reserved" : "Booked"),
                avatar: matchedBooking?.profile_img,
              }
            : { name: "Available" };

          const description = isConsumedTraining
            ? `Training Class (Session #${matchedBooking?.session_number || "1"}) – ${matchedBooking?.member_name || "Member"}`
            : isConsumed
            ? `Booked by ${matchedBooking?.member_name || "Member"}${matchedBooking?.coach_id ? ` (Coach: ${coachMap[String(matchedBooking.coach_id)] || `Coach #${matchedBooking.coach_id}`})` : ""}`
            : (slot.location || "");

          combinedEvents.push({
            id: eventId,
            title,
            start,
            end,
            color,
            description,
            tags: isConsumed ? [
              {
                label: tagLabel,
                color: "red",
              },
            ] : [],
            user,
            originalData: isConsumed
              ? {
                  ...slot,
                  ...matchedBooking,
                  id: matchedBooking?.id ? `booking_${matchedBooking.id}` : slot.id,
                  booking_id: matchedBooking?.id,
                  consumed_booking: matchedBooking,
                  is_consumed: true,
                  is_training: isConsumedTraining,
                  is_training_session: isConsumedTraining,
                  training_session_id: matchedBooking?.training_session_id || slot.training_session_id,
                  training_package_name: matchedBooking?.training_package_name || slot.training_package_name,
                  session_number: matchedBooking?.session_number || slot.session_number,
                }
              : slot,
          });
        } catch (err) {
          console.error("Failed to parse calendar slot:", slot, err);
        }
      });
    }

    // Also include direct bookings from facility_booking that didn't match any pre-slotted facility slot
    bookingList.forEach((booking: any) => {
      try {
        if (booking.is_deleted) return;
        if (matchedBookingIds.has(Number(booking.id))) {
          return; // Already merged with a pre-slotted facility schedule
        }

        const statusLower = String(booking.status || "").toLowerCase();
        if (statusLower === "cancelled" || statusLower === "rejected") return;

        const cachedDates = booking.id != null ? parsedBookingDates.get(booking.id) : null;
        const start: Date = cachedDates?.start || (
          booking.slot_start_time
            ? parseSessionDateTime(booking.slot_start_time) || new Date(booking.slot_start_time)
            : parseSessionDateTime(`${booking.booking_date}T${booking.start_time}`) || new Date(`${booking.booking_date}T${booking.start_time}`)
        );
        const end: Date = cachedDates?.end || (
          booking.slot_end_time
            ? parseSessionDateTime(booking.slot_end_time) || new Date(booking.slot_end_time)
            : addHours(start, 1)
        );

        if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

        // In-memory filter safeguards for coach and facility
        if (appliedFacilityIds.length > 0 && !appliedFacilityIds.includes(String(booking.facility_id))) {
          return;
        }
        if (appliedCoachIds.length > 0 && booking.coach_id && !appliedCoachIds.includes(String(booking.coach_id))) {
          return;
        }

        const dateStr = format(start, "yyyy-MM-dd");
        const timeStr = format(start, "HH:mm:ss");
        const timeHHmm = format(start, "HH:mm");

        // Avoid duplicate if already mapped
        if (
          existingSlotKeys.has(`${booking.facility_id}_${dateStr}_${timeStr}`) ||
          existingSlotKeys.has(`${booking.facility_id}_${dateStr}_${timeHHmm}`)
        ) {
          return;
        }

        const isTraining = isTrainingBooking(booking);

        combinedEvents.push({
          id: `booking_${booking.id}`,
          title: isTraining && booking.training_package_name
            ? `${booking.training_package_name} – ${booking.member_name || "Reserved"}`
            : `${booking.facility_name || "Facility"} – ${booking.member_name || (isTraining ? "Reserved" : "Booked")}`,
          start,
          end,
          color: "red",
          description: isTraining
            ? `Training Class (Session #${booking.session_number || "1"}) – ${booking.member_name || "Member"}`
            : `Booked by ${booking.member_name || "Member"}${booking.coach_id ? ` (Coach: ${coachMap[String(booking.coach_id)] || `Coach #${booking.coach_id}`})` : ""}`,
          tags: [
            {
              label: isTraining ? "Reserved" : (booking.status || "Booked").toUpperCase(),
              color: "red",
            },
          ],
          user: {
            name: booking.member_name || (isTraining ? "Reserved" : "Booked"),
            avatar: booking.profile_img,
          },
          originalData: {
            ...booking,
            is_training: isTraining,
            is_training_session: isTraining,
          },
        });
      } catch (err) {
        console.error("Failed to parse calendar standalone booking:", booking, err);
      }
    });

    return combinedEvents;
  }, [slotsData, bookingsData, appliedFacilityIds, appliedCoachIds, coachMap]);
  // ── Handlers ────────────────────────────────────────────────────────
  const handleCreateNew = () => {
    setSelectedDate(undefined);
    setAction("add");
    setFormData(undefined);
    setIsDialogOpen(true);
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setAction("add");
    setFormData(undefined);
    setIsDialogOpen(true);
  };

  /**
   * Edit click → GET /facility/schedule/slots/details
   *
   * Response: { data: { schedule_id, rrule_str, color, location,
   *   duration_per_slot, income_category_id, tax_group_id, member_only,
   *   advance_booking_days, break_duration, start_time (ISO), end_time (ISO),
   *   facility_id, max_attendees, price_master_id, addons[], refund_policy[] } }
   *
   * rrule_str is parsed into a recurrence object for RecurrenceSelector.
   */
  const handleEditEvent = async (event: CalendarEvent, mode: "edit" | "view" = "edit") => {
    if (!event.originalData) return;

    const slot = event.originalData;

    // Check if training session booking
    const isTraining = isTrainingBooking(slot);

    if (isTraining) {
      setSelectedTrainingBooking(slot);
      setIsTrainingSheetOpen(true);
      return;
    }

    // Check if general direct facility booking (non-training booking)
    if (String(event.id).startsWith("booking_") || (!slot?.slot_date && slot?.booking_date)) {
      setSelectedFacilityBooking(slot as FacilityBooking);
      setIsBookingDetailsOpen(true);
      return;
    }

    if (
      slot?.facility_schedule_id &&
      slot?.slot_date &&
      slot?.start_time &&
      slot?.end_time
    ) {
      try {
        const startTime = `${slot.slot_date}T${slot.start_time}`;
        const endTime = `${slot.slot_date}T${slot.end_time}`;

        const result = await getSlotDetails({
          org_id: orgId,
          schedule_id: slot.facility_schedule_id as number,
          start_time: startTime,
          end_time: endTime,
        }).unwrap();

        // API wraps response in { data: { ... } }
        const details = (result as any)?.data ?? result;

        // Parse rrule_str → recurrence config for RecurrenceSelector
        // e.g. "RRULE:FREQ=WEEKLY;BYDAY=MO,WE;UNTIL=20260507T000000Z"
        // → { frequency: "weekly", weekDays: ["MO","WE"], endCondition: "on_date", endDate: "2026-05-07" }
        const recurrence = parseRRuleStr(details.rrule_str ?? "");

        // Map into FacilitySchedule shape that the form expects
        const merged: FacilitySchedule = {
          org_id: orgId,
          general_info: {
            id: details.facility_schedule_id,
            facility_id: details.facility_id ?? 0,
            is_exclusive: details.is_exclusive ?? false,
            max_attendees: details.max_attendees,
            price_master_id: details.price_master_id,
            start_time: extractTime(details.start_time ?? ""),
            end_time: extractTime(details.end_time ?? ""),
            start_date: slot.start_date || extractDate(details.start_time ?? ""),
            end_date: slot.end_date || extractDate(details.end_time ?? ""),
            color: details.color ?? "",
            location: details.location ?? "",
            description: details.description ?? "",
            name: details.name ?? "",
            type: details.type ?? "facility_schedule",
            member_only: details.member_only ?? false,
            always_available: details.always_available ?? false,
            duration_per_slot: details.duration_per_slot ?? 0,
            break_duration: details.break_duration ?? 0,
            income_category_id: details.income_category_id,
            tax_group_id: details.tax_group_id,
            advance_booking_days: details.advance_booking_days,
            rrule_str: details.rrule_str ?? "",
            // Parsed recurrence object for RecurrenceSelector component
            recurrence: recurrence as any,
          },
          add_ons: Array.isArray(details.addons) ? details.addons : [],
          refund_policies: Array.isArray(details.refund_policy) ? details.refund_policy : [],
          tier_pricing: [],
          plan_pricing: [],
          group_pricing: [],
          special_pricing: [],
        };

        console.log(`this is merged data ${JSON.stringify(merged)}`)

        setAction(mode);
        setFormData(merged);
        setSelectedDate(undefined);
        setIsDialogOpen(true);
      } catch (error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error loading schedule details",
          description: `${typedError.data?.detail ||
            typedError.data?.message ||
            "Something went wrong"
            }`,
        });
      }
    } else {
      // Fallback: open form with existing slot data
      setAction(mode);
      setFormData(slot as FacilitySchedule);
      setSelectedDate(undefined);
      setIsDialogOpen(true);
    }
  };

  /**
   * Scope-aware delete / cancel handler
   */
  const handleDeleteEvent = async (
    event: CalendarEvent,
    scope: DeleteScope = "this"
  ) => {
    const slot = event.originalData;

    // ── 1. Training session cancellation ─────────────────────────────
    const isTraining = isTrainingBooking(slot);

    if (isTraining) {
      const sessionId =
        slot?.training_session_id ||
        slot?.consumed_booking?.training_session_id ||
        slot?.consumed_slots?.[0]?.training_session_id;

      if (!sessionId) {
        toast({
          variant: "destructive",
          title: "Cannot cancel session",
          description: "Training session ID not found for this booking.",
        });
        return;
      }

      try {
        await cancelSessionSlot(sessionId).unwrap();
        toast({
          variant: "success",
          title: "Training class cancelled",
          description:
            "The credit has been refunded to the training package and the session is ready to be rescheduled.",
        });
        refetch();
        bookingsRefetch?.();
      } catch (error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error cancelling training class",
          description: `${
            typedError.data?.detail ||
            typedError.data?.message ||
            "Something went wrong while cancelling the training class."
          }`,
        });
      }
      return;
    }

    // ── 2. Plain direct booking deletion ─────────────────────────────
    if (String(event.id).startsWith("booking_") || !slot?.slot_date) {
      const bookingId =
        slot?.booking_id ||
        slot?.consumed_booking?.id ||
        (String(event.id).startsWith("booking_")
          ? Number(String(event.id).replace("booking_", ""))
          : slot?.id);
      if (!bookingId || isNaN(Number(bookingId))) {
        toast({
          variant: "destructive",
          title: "Cannot delete",
          description: "Valid booking ID not found.",
        });
        return;
      }
      try {
        await deleteFacilityBooking(Number(bookingId)).unwrap();
        toast({
          variant: "success",
          title: "Facility Booking Deleted Successfully",
        });
        refetch();
        bookingsRefetch?.();
      } catch (error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Deletion",
          description: `${
            typedError.data?.detail ||
            typedError.data?.message ||
            "Something went wrong"
          }`,
        });
      }
      return;
    }

    // ── 3. Recurring facility schedule slot deletion ──────────────────
    if (!slot?.facility_schedule_id) {
      toast({
        variant: "destructive",
        title: "Cannot delete",
        description: "This slot has no associated schedule.",
      });
      return;
    }

    const slotDate: string = slot.slot_date;
    let scheduleStartDate: string = slot.start_date || slotDate;
    let scheduleEndDate: string = slot.end_date || slotDate;

    // Fetch schedule details to get recurrence end date
    if (slot?.facility_schedule_id && slot?.slot_date && slot?.start_time && slot?.end_time) {
      try {
        const startTime = `${slot.slot_date}T${slot.start_time}`;
        const endTime = `${slot.slot_date}T${slot.end_time}`;

        const result = await getSlotDetails({
          org_id: orgId,
          schedule_id: slot.facility_schedule_id as number,
          start_time: startTime,
          end_time: endTime,
        }).unwrap();

        const details = (result as any)?.data ?? result;
        const recurrence = parseRRuleStr(details.rrule_str ?? "");

        // Extract schedule dates from API response
        if (details.start_time) {
          scheduleStartDate = extractDate(details.start_time);
        }

        // Use recurrence end date if available, otherwise calculate from count or use API end_date
        if (recurrence?.endDate) {
          scheduleEndDate = recurrence.endDate;
        } else if (recurrence?.occurrences && details.start_time) {
          const calcEnd = deriveEndDateFromCount(
            recurrence as any,
            extractDate(details.start_time)
          );
          if (calcEnd) scheduleEndDate = calcEnd;
        } else if (details.end_date) {
          scheduleEndDate = details.end_date;
        }
      } catch (error) {
        console.warn("Could not fetch schedule details", error);
      }
    }

    let start_date: string;
    let end_date: string;

    if (scope === "this") {
      start_date = slotDate;
      end_date = slotDate;
    } else if (scope === "this_and_following") {
      start_date = slotDate;
      end_date = scheduleEndDate;
    } else {
      start_date = scheduleStartDate;
      end_date = scheduleEndDate;
    }

    try {
      await deleteFacilitySchedule({
        schedule_id: slot.facility_schedule_id as number,
        scope,
        start_date,
        end_date,
      }).unwrap();

      toast({ variant: "success", title: "Schedule deleted successfully" });
      refetch();
    } catch (error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error deleting schedule",
        description: `${typedError.data?.detail ||
          typedError.data?.message ||
          "Something went wrong"
          }`,
      });
    }
  };

  return (
    <div className="w-full h-full p-3 sm:p-5 flex flex-col gap-4">
      <Card className="flex-1 overflow-hidden overflow-x-auto" style={{ minHeight: "600px" }}>
        <FullCalendar
          events={events}
          initialView="month"
          initialDate={currentDate}
          onViewEvent={(event) => handleEditEvent(event, "view")}
          onEditEvent={(event) => handleEditEvent(event, "edit")}
          onDeleteEvent={handleDeleteEvent}
          onDateClick={handleDateClick}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onCalendarViewChange={setCalendarView}
          access={facilityScheduleAccess}
          refetch={refetch}
          isCustomView={false}
          allowGridView={false}
          isFilterApplied={
            appliedBusinessUnitIds.length > 0 ||
            appliedFacilityIds.length > 0 ||
            appliedCoachIds.length > 0
          }
          onFilterEvents={() => {
            setPendingBusinessUnitIds(appliedBusinessUnitIds);
            setPendingFacilityIds(appliedFacilityIds);
            setPendingCoachIds(appliedCoachIds);
            setIsFilterOpen(true);
          }}
        />
      </Card>

      {/* ── Business Unit, Facility & Coach Filter Sheet ──────────── */}
      <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <SheetContent className="!h-screen">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <Separator className="h-[1px] rounded-full my-2" />
          <div className="py-2 space-y-4">
            {/* Business Unit MultiSelect */}
            <MultiSelect
              floatingLabel="Business Unit"
              options={businessUnitOptions}
              defaultValue={pendingBusinessUnitIds}
              onValueChange={(selected) => setPendingBusinessUnitIds(selected as string[])}
              placeholder="Select Business Unit"
              variant="inverted"
              maxCount={2}
              className="capitalize"
            />

            {/* Facility MultiSelect */}
            <MultiSelect
              floatingLabel="Facility"
              options={facilityOptions}
              defaultValue={pendingFacilityIds}
              onValueChange={(selected) => setPendingFacilityIds(selected as string[])}
              placeholder="Select Facility"
              variant="inverted"
              maxCount={2}
              className="capitalize"
            />

             {/* Coach MultiSelect */}
            <MultiSelect
              floatingLabel="Trainer / Coach"
              options={coachOptions}
              defaultValue={pendingCoachIds}
              onValueChange={(selected) => setPendingCoachIds(selected as string[])}
              placeholder="Select Trainer / Coach"
              variant="inverted"
              maxCount={2}
              className="capitalize"
            />

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="border-primary text-black font-semibold w-full"
                onClick={() => {
                  const defaultBu = businessUnitOptions[0]?.value ? [businessUnitOptions[0].value] : [];
                  setPendingBusinessUnitIds(defaultBu);
                  setAppliedBusinessUnitIds(defaultBu);
                  setPendingFacilityIds([]);
                  setAppliedFacilityIds([]);
                  setPendingCoachIds([]);
                  setAppliedCoachIds([]);
                  setIsFilterOpen(false);
                }}
              >
                Reset
              </Button>
              <Button
                className="bg-primary text-black font-semibold w-full"
                onClick={() => {
                  setAppliedBusinessUnitIds(pendingBusinessUnitIds);
                  setAppliedFacilityIds(pendingFacilityIds);
                  setAppliedCoachIds(pendingCoachIds);
                  setIsFilterOpen(false);
                }}
              >
                <i className="fa fa-filter mr-2" />
                Apply Filter
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <FacilityScheduleForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        data={formData}
        action={action}
        setAction={setAction}
        setData={setFormData}
        refetch={refetch}
        selectedDate={selectedDate}
      />

      <TrainingSessionSheet
        isOpen={isTrainingSheetOpen}
        onClose={() => {
          setIsTrainingSheetOpen(false);
          setSelectedTrainingBooking(null);
        }}
        booking={selectedTrainingBooking}
        coachName={
          selectedTrainingBooking?.coach_id
            ? coachMap[String(selectedTrainingBooking.coach_id)]
            : undefined
        }
        orgId={Number(orgId)}
        onSuccess={() => {
          refetch();
          bookingsRefetch?.();
        }}
      />

      <BookingRequestDetails
        isOpen={isBookingDetailsOpen}
        onClose={() => {
          setIsBookingDetailsOpen(false);
          setSelectedFacilityBooking(null);
        }}
        data={selectedFacilityBooking}
        refetch={() => {
          refetch();
          bookingsRefetch?.();
        }}
      />
    </div>
  );
};

export default FacilityScheduleCalendarView;